<template>
  <transition name="subscribe-pay-range-dialog" appear>
    <div
      class="stprd__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-pay-range-dialog-title"
      @mousedown.self="onCancel"
    >
      <div class="stprd__card">
        <div class="stprd__header">
          <div class="stprd__header-row">
            <h2 id="subscribe-pay-range-dialog-title" class="stprd__title">
              Subscribe to Pay Range
            </h2>
            <button
              type="button"
              class="stprd__close-btn"
              aria-label="Close"
              :disabled="submitting"
              @click="onCancel"
            >
              <Icon name="popup_close" />
            </button>
          </div>
          <p v-if="positionSubtitle" class="stprd__subtitle">
            {{ positionSubtitle }}
          </p>
        </div>

        <div class="stprd__body">
          <div class="stprd__field">
            <label class="stprd__label" for="subscribe-pay-range-picker">
              Pay Range
            </label>
            <PayRangePickerField
              id="subscribe-pay-range-picker"
              :model-value="payRangeId"
              :results="payRangeOptions"
              :loading="payRangeSearchLoading"
              :disabled="submitting"
              open-on-focus
              autofocus
              @update:modelValue="onPayRangeChange"
              @search="onSearch"
            />
          </div>

          <div
            v-if="payRangeId && selectedPayRangePreview"
            class="stprd__preview"
          >
            <div v-if="previewMetaLine" class="stprd__preview-meta-row">
              <img
                v-if="previewFlagSrc"
                :src="previewFlagSrc"
                alt=""
                class="stprd__preview-flag"
              />
              <span class="stprd__preview-meta">{{ previewMetaLine }}</span>
            </div>
            <div class="stprd__preview-readiness">
              <span class="stprd__anchor stprd__anchor--0">{{ readinessMin }}</span>
              <span class="stprd__readiness-sep">-</span>
              <span class="stprd__anchor stprd__anchor--50">{{ readinessAt50 }}</span>
              <span class="stprd__readiness-sep">-</span>
              <span class="stprd__anchor stprd__anchor--100">{{ readinessAt100 }}</span>
              <span class="stprd__readiness-sep">-</span>
              <span class="stprd__anchor stprd__anchor--150">{{ readinessMax }}</span>
            </div>
          </div>

          <div class="stprd__field">
            <label class="stprd__label" id="subscribe-effective-date-label" for="subscribe-effective-date">
              Effective Date
            </label>
            <div
              class="stprd__date-field"
              :class="{
                'stprd__date-field--empty': !effectiveDate,
                'stprd__date-field--locked': submitting,
              }"
            >
              <input
                ref="dateInputRef"
                id="subscribe-effective-date"
                type="date"
                class="stprd__date-input-native"
                :value="effectiveDate || ''"
                v-bind="effectiveDateInputBounds"
                :disabled="submitting"
                tabindex="-1"
                aria-hidden="true"
                @input="onEffectiveDateInput"
              />
              <button
                type="button"
                class="stprd__date-display"
                :disabled="submitting"
                aria-labelledby="subscribe-effective-date-label"
                @click="openDatePicker"
              >
                {{ effectiveDateLabel }}
              </button>
            </div>
          </div>

          <div
            v-if="showConflictCallout"
            class="stprd__callout"
            role="status"
          >
            <p class="stprd__callout-title">{{ conflictCopy.title }}</p>
            <p class="stprd__callout-body">{{ conflictCopy.body }}</p>
          </div>
        </div>

        <div class="stprd__footer">
          <div class="stprd__footer-action">
            <StandardButton
              type="magenta"
              variant="outline"
              size="full-width"
              label="Cancel"
              :disabled="submitting"
              @click="onCancel"
            />
          </div>
          <div
            class="stprd__footer-action stprd__footer-subscribe"
            :class="{
              'stprd__footer-subscribe--inactive': !canSubscribe && !submitting,
              'stprd__footer-subscribe--submitting': submitting,
            }"
          >
            <StandardButton
              type="magenta"
              variant="primary"
              size="full-width"
              :label="submitting ? 'Subscribing…' : 'Subscribe'"
              :disabled="!canSubscribe || submitting"
              :loaderLeft="submitting"
              @click="onSubscribe"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import PayRangePickerField from "@components/PayRangePickerField/PayRangePickerField.vue";
import {
  buildConflictCopy,
  buildPositionSubtitle,
  formatCompactAmount,
  defaultEffectiveDateForPublishedVersions,
  getPublishedVersionDateBounds,
  isDateCoveredByPublishedVersions,
  isEffectiveDateWithinSubscription,
  previewMetaWithoutLeadingFlag,
  resolveFlagFromMeta,
  resolveFlagSrc,
} from "./subscribeToPayRangeUtils.js";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const props = defineProps({
  position: {
    type: Object,
    required: true,
    validator: (value) =>
      value &&
      typeof value.id === "string" &&
      typeof value.code === "string" &&
      typeof value.name === "string" &&
      typeof value.department === "string",
  },
  payRangeId: {
    type: String,
    default: null,
  },
  payRangeOptions: {
    type: Array,
    default: () => [],
  },
  payRangeSearchLoading: {
    type: Boolean,
    default: false,
  },
  selectedPayRangePreview: {
    type: Object,
    default: null,
  },
  effectiveDate: {
    type: String,
    default: null,
  },
  existingSubscription: {
    type: Object,
    default: null,
  },
  publishedVersionRanges: {
    type: Array,
    default: () => [],
  },
  submitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:payRangeId",
  "search",
  "update:effectiveDate",
  "subscribe",
  "cancel",
]);

const dateInputRef = ref(null);

const positionSubtitle = computed(() => buildPositionSubtitle(props.position));

const canSubscribe = computed(
  () =>
    Boolean(
      props.payRangeId &&
        props.effectiveDate &&
        isDateCoveredByPublishedVersions(props.effectiveDate, props.publishedVersionRanges)
    )
);

const effectiveDateInputBounds = computed(() => {
  const { minDate, maxDate } = getPublishedVersionDateBounds(props.publishedVersionRanges);
  const bounds = {};
  if (minDate) bounds.min = minDate;
  if (maxDate) bounds.max = maxDate;
  return bounds;
});

const showConflictCallout = computed(
  () =>
    Boolean(
      props.effectiveDate &&
        props.existingSubscription &&
        isEffectiveDateWithinSubscription(props.effectiveDate, props.existingSubscription)
    )
);

const conflictCopy = computed(() =>
  buildConflictCopy(props.existingSubscription, props.effectiveDate)
);

const readinessMin = computed(() =>
  formatCompactAmount(props.selectedPayRangePreview?.readiness?.min)
);
const readinessAt50 = computed(() =>
  formatCompactAmount(props.selectedPayRangePreview?.readiness?.at50)
);
const readinessAt100 = computed(() =>
  formatCompactAmount(props.selectedPayRangePreview?.readiness?.at100)
);
const readinessMax = computed(() =>
  formatCompactAmount(props.selectedPayRangePreview?.readiness?.max)
);

const previewFlagSrc = computed(() => {
  const previewFlag = props.selectedPayRangePreview?.flag;
  if (previewFlag) return resolveFlagSrc(previewFlag);

  const selectedOption = props.payRangeOptions.find(
    (option) => option.id === props.payRangeId
  );
  if (selectedOption?.flag) return resolveFlagSrc(selectedOption.flag);

  const meta = props.selectedPayRangePreview?.meta;
  return resolveFlagSrc(resolveFlagFromMeta(meta));
});

const previewMetaLine = computed(() => {
  const meta = props.selectedPayRangePreview?.meta;
  if (!meta) return "";
  return previewFlagSrc.value ? previewMetaWithoutLeadingFlag(meta) : meta;
});

const effectiveDateLabel = computed(() =>
  props.effectiveDate ? formatFairPayDate(props.effectiveDate) : "Select a date"
);

function openDatePicker() {
  if (props.submitting) return;
  const input = dateInputRef.value;
  if (!input) return;
  if (typeof input.showPicker === "function") {
    input.showPicker();
    return;
  }
  input.focus();
  input.click();
}

function syncEffectiveDateToCoverage() {
  const next = defaultEffectiveDateForPublishedVersions(
    props.publishedVersionRanges,
    props.effectiveDate
  );
  if (next && next !== props.effectiveDate) {
    emit("update:effectiveDate", next);
  }
}

watch(
  () => props.publishedVersionRanges,
  syncEffectiveDateToCoverage,
  { deep: true }
);

function onPayRangeChange(value) {
  emit("update:payRangeId", value);
}

function onSearch(query) {
  emit("search", query);
}

function onEffectiveDateInput(event) {
  emit("update:effectiveDate", event.target.value || null);
}

function onCancel() {
  if (props.submitting) return;
  emit("cancel");
}

function onSubscribe() {
  if (!canSubscribe.value || props.submitting) return;
  emit("subscribe", {
    payRangeId: props.payRangeId,
    effectiveDate: props.effectiveDate,
  });
}
</script>

<style scoped>
.stprd__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.stprd__card {
  width: 440px;
  max-width: 100%;
  max-height: 95vh;
  overflow-y: auto;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 8px 16px -4px rgba(16, 24, 40, 0.1);
  display: flex;
  flex-direction: column;
  font-family: Inter, sans-serif;
}

.stprd__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 24px 24px 20px;
}

.stprd__header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.stprd__title {
  margin: 0;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.stprd__subtitle {
  margin: 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
}

.stprd__close-btn {
  appearance: none;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  padding: 8px;
  margin: 0;
  cursor: pointer;
  line-height: 0;
  color: #98a2b3;
  border-radius: 8px;
}

.stprd__close-btn:hover:not(:disabled) {
  background: rgba(152, 162, 179, 0.12);
}

.stprd__close-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.stprd__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.stprd__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 24px 24px;
}

.stprd__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stprd__label {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}

.stprd__date-field {
  position: relative;
  width: 100%;
}

.stprd__date-input-native {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

.stprd__date-display {
  appearance: none;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  text-align: left;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  cursor: pointer;
}

.stprd__date-field--empty .stprd__date-display {
  color: #667085;
}

.stprd__date-field:not(.stprd__date-field--empty) .stprd__date-display {
  color: #101828;
}

.stprd__date-field--locked .stprd__date-display {
  background: #f9fafb;
  color: #101828;
  cursor: not-allowed;
}

.stprd__date-display:focus-visible {
  outline: none;
  border-color: #d6bbfb;
  box-shadow:
    0 0 0 4px #f4ebff,
    0 1px 2px rgba(16, 24, 40, 0.05);
}

.stprd__date-display:disabled {
  cursor: not-allowed;
}

.stprd__preview-meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.stprd__preview-flag {
  width: 16px;
  height: auto;
  border-radius: 1px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.stprd__preview-meta {
  margin: 0;
  color: #667085;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  min-width: 0;
}

.stprd__preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f9fafb;
}

.stprd__preview-readiness {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
}

.stprd__readiness-sep {
  color: #344054;
}

.stprd__anchor--0 {
  color: #344054;
}

.stprd__anchor--50 {
  color: #ff0303;
}

.stprd__anchor--100 {
  color: #17b26a;
}

.stprd__anchor--150 {
  color: #00a3ff;
}

.stprd__callout {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #fde3a8;
  background: #fffcf5;
  color: #b54708;
  font-size: 13px;
  line-height: normal;
}

.stprd__callout-title {
  margin: 0;
  font-weight: 600;
}

.stprd__callout-body {
  margin: 0;
  font-weight: 400;
}

.stprd__footer {
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding: 32px 24px 24px;
}

.stprd__footer-action {
  flex: 1 1 0;
  min-width: 0;
}

.stprd__footer-action ::v-deep(.standard-button) {
  width: 100%;
  box-sizing: border-box;
}

.stprd__footer-subscribe--inactive ::v-deep(.standard-button.standard-button--disabled) {
  background-color: #eaecf0;
  border-color: #eaecf0;
  color: #98a2b0;
}

.stprd__footer-subscribe--submitting ::v-deep(.standard-button.standard-button--disabled) {
  background-color: #c2a8eb;
  border-color: #c2a8eb;
  color: #ffffff;
}

.subscribe-pay-range-dialog-enter-active,
.subscribe-pay-range-dialog-leave-active {
  transition: opacity 180ms ease;
}

.subscribe-pay-range-dialog-enter-from,
.subscribe-pay-range-dialog-leave-to {
  opacity: 0;
}

.subscribe-pay-range-dialog-enter-active .stprd__card,
.subscribe-pay-range-dialog-leave-active .stprd__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
  will-change: transform, opacity;
}

.subscribe-pay-range-dialog-enter-from .stprd__card,
.subscribe-pay-range-dialog-leave-to .stprd__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}
</style>
